import Decimal from 'decimal.js';
import { useMemo } from 'react';

import {
  EstateWaterfall_EdgeFragment,
  EstateWaterfall_TransferLogItemFragment,
} from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { FUNCTIONAL } from '@/styles/tokens/colors';
import {
  EstateWaterfallTransferKind,
  HypotheticalSaleLoanPretermGrantorDeathHandling,
  SaleOrLoanType,
} from '@/types/schema';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EdgeLabelCard, EdgeLabelCardProps } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface CreateHypotheticalSaleLoanCardParams {
  edge: EstateWaterfall_EdgeFragment;
  sourceEntityName: string;
  targetEntityName: string;
  transferLogItem: EstateWaterfall_TransferLogItemFragment;
  totalPayments: Decimal;
}

function getCardProps({
  sourceEntityName,
  targetEntityName,
  isLoan,
  transactionType,
  totalPayments,
  fundingAmount,
  termEndYear,
  pretermGrantorDeathHandling,
}: {
  sourceEntityName: string;
  targetEntityName: string;
  isLoan: boolean;
  transactionType: string;
  totalPayments: Decimal;
  fundingAmount: Decimal;
  termEndYear: number;
  pretermGrantorDeathHandling?: HypotheticalSaleLoanPretermGrantorDeathHandling;
}): EdgeLabelCardProps {
  const deathDescription =
    pretermGrantorDeathHandling ===
    HypotheticalSaleLoanPretermGrantorDeathHandling.Repayment
      ? 'Liability will be repaid in full upon death if grantor dies before term end'
      : 'Liability will be canceled upon death if grantor dies before term end';

  // Use sourceEntityName and targetEntityName to personalize the description
  const description = isLoan
    ? `${sourceEntityName} loans assets to ${targetEntityName} and receives note payments in return. ${deathDescription}.`
    : `${sourceEntityName} sells assets to ${targetEntityName} and receives note payments in return. ${deathDescription}.`;

  return {
    title: `Hypothetical ${transactionType}`,
    titleSx: { color: FUNCTIONAL.WARNING[600] },
    description: description,
    rows: [
      {
        label: `${transactionType} amount`,
        value: formatCurrencyNoDecimals(fundingAmount),
      },
      {
        label: 'Total payments',
        value: formatCurrencyNoDecimals(totalPayments),
      },
      ...(!!termEndYear
        ? [
            {
              label: 'Term end',
              value: `${termEndYear.toString()}`,
            },
          ]
        : []),
    ],
  };
}

function SaleLoanCard(props: CreateHypotheticalSaleLoanCardParams) {
  const { sourceEntityName, targetEntityName, transferLogItem, totalPayments } =
    props;

  const associatedHypotheticalSaleLoan = useMemo(() => {
    if (
      transferLogItem.associatedObject?.__typename === 'HypotheticalSaleLoan'
    ) {
      return transferLogItem.associatedObject;
    }

    return null;
  }, [transferLogItem.associatedObject]);

  const termEndYear = useMemo(() => {
    if (
      associatedHypotheticalSaleLoan &&
      associatedHypotheticalSaleLoan.startDate &&
      associatedHypotheticalSaleLoan.termLengthYears
    ) {
      return (
        associatedHypotheticalSaleLoan.startDate.getFullYear() +
        associatedHypotheticalSaleLoan.termLengthYears
      );
    }

    return 0;
  }, [associatedHypotheticalSaleLoan]);

  const cardProps = useMemo(() => {
    return {
      sourceEntityName,
      targetEntityName,
      isLoan:
        transferLogItem.kind ===
        EstateWaterfallTransferKind.HypotheticalSaleLoan,
      transactionType:
        transferLogItem.saleOrLoanType === SaleOrLoanType.Loan
          ? 'Loan'
          : 'Sale',
      fundingAmount: transferLogItem.finalTransferAmount ?? new Decimal(0),
      totalPayments,
      termEndYear,
      pretermGrantorDeathHandling:
        associatedHypotheticalSaleLoan?.pretermGrantorDeathHandling,
    };
  }, [
    sourceEntityName,
    targetEntityName,
    transferLogItem.kind,
    transferLogItem.saleOrLoanType,
    transferLogItem.finalTransferAmount,
    totalPayments,
    termEndYear,
    associatedHypotheticalSaleLoan?.pretermGrantorDeathHandling,
  ]);

  return <EdgeLabelCard {...getCardProps(cardProps)} />;
}

export function createHypotheticalSaleLoanCard(
  params: CreateHypotheticalSaleLoanCardParams
): CardConfig {
  const { edge, transferLogItem } = params;

  // Determine if it's a loan or sale
  const transactionType =
    transferLogItem.saleOrLoanType === SaleOrLoanType.Loan ? 'Loan' : 'Sale';

  return {
    element: (
      <SaleLoanCard
        key={`${edge.from.id}-${edge.to.id}-${transferLogItem.associatedObjectID}`}
        {...params}
      />
    ),
    label: `Hypothetical ${transactionType}`,
    value: formatCurrency(
      transferLogItem.finalTransferAmount ?? new Decimal(0),
      { notation: 'compact' }
    ),
    variant: 'secondary',
  };
}
